import cx from 'classnames';
import { forwardRef, useMemo } from 'react';
import type { ReactNode, RefObject } from 'react';
import useResizeObserver from 'use-resize-observer';

import type { PanelNames } from '@trello/split-screen';
import { useSplitScreenSharedState } from '@trello/split-screen';
import { getTestId } from '@trello/test-ids';
import type { SplitScreenTestIds } from '@trello/test-ids';

import * as styles from './SplitScreenPanel.module.less';

interface SplitScreenPanelProps {
  children?: ReactNode;
  panelName: PanelNames;
  isHidden?: boolean;
  panelWidth?: number;
  enableTransitions?: boolean;
  isCollapsed?: boolean;
  shouldGrow?: boolean;
  onResize: () => void;
}

export const SplitScreenPanel = forwardRef<
  HTMLDivElement,
  SplitScreenPanelProps
>(
  (
    {
      children,
      isHidden,
      panelName,
      panelWidth,
      enableTransitions,
      isCollapsed,
      shouldGrow = false,
      onResize,
    },
    ref,
  ) => {
    const { areMultiplePanelsOpen } = useSplitScreenSharedState();

    useResizeObserver({
      ref: ref as RefObject<HTMLDivElement> | null,
      onResize,
    });

    const panelFlexValue = useMemo(() => {
      const width = `${panelWidth ?? 0}px`;

      if (isHidden || panelWidth === 0) {
        return '0 0 0px';
      }

      if (!areMultiplePanelsOpen || shouldGrow) {
        return `1 1 ${width}`;
      }

      return `0 0 ${width}`;
    }, [isHidden, panelWidth, areMultiplePanelsOpen, shouldGrow]);

    return (
      <div
        className={cx(styles.panel, {
          [styles.singlePanel]: !areMultiplePanelsOpen,
          [styles.panelTransitions]: enableTransitions,
          [styles.hidden]: isHidden,
          [styles.collapsed]: isCollapsed,
        })}
        data-testid={getTestId<SplitScreenTestIds>('split-screen-panel')}
        style={{
          flex: panelFlexValue,
        }}
        ref={ref}
        aria-hidden={isHidden}
      >
        {children}
      </div>
    );
  },
);
